import { all, call, put, fork, takeLatest } from 'redux-saga/effects';
import { settingsActions as actions } from '.';

import api from 'app/api';
import {
  AddWebhhokType,
  CreateBizEmployeeType,
  EmbeddedSignUpType,
  PayloadRolePermission,
  WhatsappRegisterType,
} from './types';
import { authPageActions } from 'app/pages/Auth/slice';
import { successModal } from 'utils/showSuccessErrorModal';
import { QuickReplyMessageType } from 'app/pages/Inbox/slice/types';

export function* whatsappRegister(action: {
  payload: WhatsappRegisterType;
  type: string;
}) {
  try {
    const response = yield call(
      api.post,
      `/business/whatsapp-contact-details`,
      action.payload,
    );
    yield put(actions.whatsappRegisterSuccess(response.data));
    yield put(authPageActions.getBusinessDetails());
    setTimeout(() => {
      window.location.reload();
    }, 4000);
  } catch (err: any) {
    yield put(actions.whatsappRegisterFailure(err?.response?.data));
  }
}

export function* whatsappEmbeddedSignUp(action: {
  payload: EmbeddedSignUpType;
  type: string;
}) {
  try {
    const response = yield call(
      api.post,
      `/business/embedded-signup`,
      action.payload,
    );
    yield put(actions.whatsappRegisterSuccess(response.data));
    yield put(authPageActions.getBusinessDetails());
    setTimeout(() => {
      window.location.reload();
    }, 4000);
  } catch (err: any) {
    yield put(actions.whatsappRegisterFailure(err?.response?.data));
  }
}

export function* createBizEmployee(action: {
  payload: CreateBizEmployeeType;
  type: string;
}) {
  try {
    const response = yield call(
      api.post,
      `/auth/business-employees`,
      action.payload,
    );
    yield put(actions.createBizEmployeeSuccess(response.data));
    // Refetch the roster so the new/updated row carries its real
    // id/status/rolePermission (the create response is a summary, not a full
    // BizEmployee row). SILENT so the full-screen loader doesn't unmount the
    // create form before its close-on-success effect runs (success → close +
    // table; the table then updates from this refetch).
    yield put(actions.fetchBizEmployeesStart({ silent: true }));
  } catch (err: any) {
    yield put(actions.createBizEmployeeFailure(err?.response?.data));
  }
}

export function* generateApiKey(action: {
  payload: { expiresIn: number };
  type: string;
}) {
  try {
    const response = yield call(api.put, `/business/api-key`, action.payload);
    yield put(actions.generateApiKeySuccess(response.data));
    yield put(authPageActions.getBusinessDetails());
  } catch (err: any) {
    yield put(actions.generateApiKeyFailure(err?.response?.data));
  }
}

export function* deleteWebhook(action: {
  payload: { url: string };
  type: string;
}) {
  try {
    const response = yield call(
      api.delete,
      `business/webhook-url?url=${action.payload.url}`,
    );
    yield put(actions.deleteWebhookUrlSuccess(response.data));
    yield put(authPageActions.getBusinessDetails());
  } catch (err: any) {
    yield put(actions.deleteWebhookUrlFailure(err?.response?.data));
  }
}

export function* addOrUpdateWebhookUrl(action: {
  payload: {
    urls: AddWebhhokType[];
    message?: string;
  };
  type: string;
}) {
  try {
    const response = yield call(api.post, `business/webhook-url`, {
      urls: action.payload.urls,
    });
    if (response.data && action.payload.message) {
      response.data.message = action.payload.message;
    }
    yield put(actions.addOrUpdateWebhookUrlSuccess(response.data));
    yield put(authPageActions.getBusinessDetails());
  } catch (err: any) {
    yield put(actions.addOrUpdateWebhookUrlFailure(err?.response?.data));
  }
}

export function* updateBizEmployee(action: {
  payload: { emailId: string; role: string };
  type: string;
}) {
  try {
    const response = yield call(
      api.put,
      `/auth/business-employees/${action.payload.emailId}`,
      { role: action.payload.role },
    );
    yield put(actions.updateBizEmployeeSuccess(response.data));
    // one can update its own too
    yield put(authPageActions.updateJwtTokenStart());
  } catch (err: any) {
    yield put(actions.updateBizEmployeeFailure(err?.response?.data));
  }
}

export function* deleteBizEmployee(action: {
  payload: { emailId: string };
  type: string;
}) {
  try {
    const response = yield call(
      api.delete,
      `/auth/business-employees/${action.payload.emailId}`,
    );
    yield put(actions.deleteBizEmployeeSuccess(response.data));
  } catch (err: any) {
    yield put(actions.deleteBizEmployeeFailure(err?.response?.data));
  }
}

export function* resendBizEmployeeInvite(action: {
  payload: { emailId: string };
  type: string;
}) {
  try {
    const response = yield call(
      api.post,
      `/auth/business-employees/${encodeURIComponent(
        action.payload.emailId,
      )}/resend-invite`,
    );
    yield put(actions.resendBizEmployeeInviteSuccess(response.data));
  } catch (err: any) {
    yield put(actions.resendBizEmployeeInviteFailure(err?.response?.data));
  }
}

export function* fetchBizEmployees() {
  try {
    const response = yield call(api.get, `/auth/business-employees`);
    yield put(actions.fetchBizEmployeesSuccess(response.data.data));
  } catch (err: any) {
    yield put(actions.fetchBizEmployeesFailure(err?.response?.data));
  }
}

export function* updateQuickReplies(action: {
  payload: { updatedReplies: QuickReplyMessageType[]; employeeEmail: string };
  type: string;
}) {
  try {
    const { updatedReplies, employeeEmail } = action.payload;
    const response = yield call(
      api.put,
      '/auth/business-employees/quick-replies',
      {
        email: employeeEmail,
        quickReplies: updatedReplies,
      },
    );
    yield put(
      actions.updateBizEmployeeKeyValueSuccess({
        email: employeeEmail,
        data: { metaData: { quickReplies: response.data.data.quickReplies } },
      }),
    );
    successModal('Success');
  } catch (error: any) {
    yield put(actions.updateBizEmployeeKeyValueFailure(error?.response?.data));
  }
}

function* updateRolePermissionList(action: {
  payload: PayloadRolePermission[];
  type: string;
}) {
  try {
    const response = yield call(api.post, `/role-permission`, action.payload);
    yield put(authPageActions.getRolePermissionListSuccess(response.data.data));
    successModal(response.data.message);
  } catch (err: any) {
    yield put(actions.fetchBizEmployeesFailure(err?.response?.data));
  }
}

function* watchWhatsappRegister() {
  yield takeLatest(actions.whatsappRegisterStart.type, whatsappRegister);
}

function* watchWhatsappEmbeddedSignUp() {
  yield takeLatest(
    actions.whatsappEmbededSignUpStart.type,
    whatsappEmbeddedSignUp,
  );
}

function* watchCreateBizEmployee() {
  yield takeLatest(actions.createBizEmployeeStart.type, createBizEmployee);
}

function* watchGenerateApiKey() {
  yield takeLatest(actions.generateApiKeyStart.type, generateApiKey);
}

function* watchDeleteWebhook() {
  yield takeLatest(actions.deleteWebhookUrlStart.type, deleteWebhook);
}

function* watchAddOrUpdateWebhookUrl() {
  yield takeLatest(
    actions.addOrUpdateWebhookUrlStart.type,
    addOrUpdateWebhookUrl,
  );
}

function* watchFetchBizEmployees() {
  yield takeLatest(actions.fetchBizEmployeesStart.type, fetchBizEmployees);
}

function* watchUpdateBizEmployee() {
  yield takeLatest(actions.updateBizEmployeeStart.type, updateBizEmployee);
}

function* watchDeleteBizEmployee() {
  yield takeLatest(actions.deleteBizEmployeeStart.type, deleteBizEmployee);
}

function* watchResendBizEmployeeInvite() {
  yield takeLatest(
    actions.resendBizEmployeeInviteStart.type,
    resendBizEmployeeInvite,
  );
}

function* watchUpdateQuickReplies() {
  yield takeLatest(actions.updateQuickRepliesStart.type, updateQuickReplies);
}

function* watchUpdateRolePermissionList() {
  yield takeLatest(
    actions.updateRolePermissionStart.type,
    updateRolePermissionList,
  );
}

export function* settingsSaga() {
  const sagas = [
    watchWhatsappRegister,
    watchWhatsappEmbeddedSignUp,
    watchCreateBizEmployee,
    watchGenerateApiKey,
    watchDeleteWebhook,
    watchAddOrUpdateWebhookUrl,
    watchFetchBizEmployees,
    watchUpdateBizEmployee,
    watchDeleteBizEmployee,
    watchResendBizEmployeeInvite,
    watchUpdateQuickReplies,
    watchUpdateRolePermissionList,
  ];

  yield all(
    sagas.map(saga =>
      fork(function* () {
        while (true) {
          try {
            yield call(saga);
            break;
          } catch (e) {
            console.error('Saga error in settingsSaga:', e);
          }
        }
      }),
    ),
  );
}
